import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Dialog, DialogActions, DialogContent, DialogTitle,
} from "@mui/material";
import { SaveOutlined } from "@mui/icons-material";
import AppSnackbar from "components/common/snackbar/AppSnackbar";
import { AppButton } from "components/common/buttons";
import {
  AppAutocomplete, AppSelect, AppTextField, shouldUseAutocomplete,
} from "components/common/inputs";
import dayjs from "dayjs";

export default function EntityFormDialog({
  open,
  onClose,
  title,
  fields = [],
  initialData = {},
  submitLabel = "Lưu",
  onSubmit,
  transformForm,
  maxWidth = "sm",
  children,
}) {
  const [form, setForm] = useState(initialData);
  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });
  const [submitting, setSubmitting] = useState(false);

  const buildInitialForm = useCallback((dataSource = {}) => {
    const data = { ...dataSource };
    const baseForm = transformForm ? transformForm(data) : data;
    const initialFields = typeof fields === "function" ? fields(baseForm) : fields;

    initialFields.forEach((field) => {
      if (
        field.type === "date" &&
        field.defaultToday &&
        !data[field.name]
      ) {
        data[field.name] = dayjs().format("YYYY-MM-DD");
      }
    });

    return data;
  }, [fields, transformForm]);

  useEffect(() => {
    if (open) {
      setForm(buildInitialForm(initialData || {}));
      setErrors({});
      setSnackbar({ open: false, message: "", severity: "info" });
    }
  }, [buildInitialForm, initialData, open]);

  const resolvedForm = useMemo(() => transformForm ? transformForm(form) : form, [form, transformForm]);
  const resolvedFields = useMemo(
    () => (typeof fields === "function" ? fields(resolvedForm) : fields),
    [fields, resolvedForm],
  );

  const update = (name, value) => {
    setForm((current) => ({
      ...current,
      [name]: value,
      ...(name === "sectionId" ? { scopeId: "", itemId: "" } : {}),
      ...(name === "scopeId" ? { itemId: "" } : {}),
    }));
    setErrors((current) => ({ ...current, [name]: "" }));
  };

  const submit = async () => {
    const validation = {};
    resolvedFields.forEach((field) => {
      const value = resolvedForm[field.name];
      if (field.required && (value === "" || value === null || value === undefined)) {
        validation[field.name] = `${field.label} là bắt buộc`;
      }
      if (field.validate) {
        const message = field.validate(value, resolvedForm);
        if (message) validation[field.name] = message;
      }
    });
    setErrors(validation);
    if (Object.keys(validation).length) return;

    setSubmitting(true);
    setSnackbar((current) => ({ ...current, open: false }));
    try {
      await onSubmit?.(resolvedForm);
      onClose?.();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || error.message || "Không thể lưu dữ liệu.",
        severity: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth={maxWidth} fullWidth className="subcontract-dialog">
      <DialogTitle variant="h6" fontWeight={600}>{title}</DialogTitle>
      <DialogContent className="subcontract-form-grid" sx={{ pt: "14px !important" }}>
        {resolvedFields.map((field) => {
          const value = resolvedForm[field.name] ?? "";
          if (field.options) {
            const Dropdown = shouldUseAutocomplete(field.options, field.autocomplete)
              ? AppAutocomplete
              : AppSelect;
            return <Dropdown
              key={field.name}
              className={field.full ? "full" : ""}
              label={field.label}
              value={value}
              options={field.options}
              required={field.required}
              disabled={field.disabled}
              error={Boolean(errors[field.name])}
              helperText={errors[field.name] || field.helperText}
              placeholder={field.placeholder}
              allowClear={!field.required}
              onChange={(selectedValue) => update(field.name, selectedValue)}
            />;
          }

          return (
            <AppTextField
              key={field.name}
              className={field.full ? "full" : ""}
              label={field.label}
              required={field.required}
              type={field.type || "text"}
              defaultToday={field.defaultToday}
              multiline={field.multiline}
              minRows={field.multiline ? field.minRows || 3 : undefined}
              value={value}
              disabled={field.disabled}
              error={Boolean(errors[field.name])}
              helperText={errors[field.name] || field.helperText}
              inputProps={field.inputProps}
              onChange={(nextValue) => update(field.name, nextValue)}
            />
          );
        })}
        {children && (
          <div className="full">
            {children}
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <AppButton color="inherit" onClick={onClose} disabled={submitting}>Hủy</AppButton>
        <AppButton variant="contained" startIcon={<SaveOutlined />} onClick={submit} disabled={submitting}>
          {submitting ? "Đang lưu..." : submitLabel}
        </AppButton>
      </DialogActions>
      <AppSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
      />
    </Dialog>
  );
}
